import { ElementInspector } from '@client/components/_core/dev-tools';
import { RouterErrorComponent } from '@client/components/_core/error-handling/error/RouterErrorComponent';
import { PageController } from '@client/components/_core/layout/PageController';
import { AppProvider } from '@client/components/_core/providers/AppProvider';
import { TrpcProvider } from '@client/components/_core/providers/TrpcProvider';
import { UIProvider } from '@client/components/_core/providers/UIProvider';
import { DeferredProductFruitsSetup } from '@client/components/_core/utils/DeferredProductFruitsSetup';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { NotFound } from '@client/components/NotFound';
import { useHasRouteAccess } from '@client/hooks/navigation/useHasRouteAccess';
import type { FileRouteTypes } from '@client/routeTree.gen';
import type { RouteContext } from '@client/types/router';
import { normalizeRoutePath } from '@client/utils/common/navigation';
import { deferredRoutesBeforeLoad, ROOT_PENDING_MS } from '@client/utils/routing/deferred-route-loader';
import { createRootRouteWithContext, stripSearchParams, useMatch, useMatches } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type PropsWithChildren, useMemo } from 'react';
import { z } from 'zod';

const DEFAULT_VALUES = { search: '' };

export const Route = createRootRouteWithContext<RouteContext>()({
    validateSearch: zodValidator(z.object({ search: z.string().default(DEFAULT_VALUES.search) })),
    search: { middlewares: [stripSearchParams(DEFAULT_VALUES)] },
    // The admin console's routes are not in the tree the app boots with. An in-app navigation
    // into it loads them here and restarts, so it is matched against the grown route table.
    beforeLoad: ({ location, preload, abortController }): void | Promise<void> =>
        deferredRoutesBeforeLoad({ location, preload, abortController }),
    // Meanwhile the page being left stays on screen instead of the pending component.
    pendingMs: ROOT_PENDING_MS,
    component: Root,
    errorComponent: RouterErrorComponent,
});

function ProtectedRoutes({ children }: PropsWithChildren) {
    const matches = useMatches();
    const consoleMatch = useMatch({ from: '/console/$orgId', shouldThrow: false });
    const adminConsoleMatch = useMatch({ from: '/admin-console', shouldThrow: false });
    const hasRouteAccess = useHasRouteAccess();

    // Check if current route is an authenticated route (console or admin)
    const isAuthenticatedRoute = Boolean(consoleMatch || adminConsoleMatch);

    // For authenticated routes, let RequireAuth component handle authentication
    // For public routes, check route-specific access permissions
    const shouldCheckAccess = !isAuthenticatedRoute;

    const hasAccess = useMemo(() => {
        if (!shouldCheckAccess) return true;

        // Check access for all matched routes
        // This ensures nested routes with different permissions are handled correctly
        return matches.every((match) => {
            try {
                // Normalize the path to remove trailing slashes (layout routes have trailing slashes)
                return hasRouteAccess(normalizeRoutePath(match.fullPath) as FileRouteTypes['to']);
            } catch (error) {
                console.error('Error checking route access:', error);
                return false;
            }
        });
    }, [shouldCheckAccess, matches, hasRouteAccess]);

    // Show loading state if we're checking access
    if (shouldCheckAccess && typeof hasAccess === 'undefined') {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loading size="large" />
            </div>
        );
    }

    // For authenticated routes (console/admin), return children immediately
    // The RequireAuth component in the route tree will handle authentication
    if (isAuthenticatedRoute) {
        return <>{children}</>;
    }

    // For public routes, check access and show NotFound if denied
    return hasAccess ? children : <NotFound />;
}

function Root() {
    return (
        <PageController route={Route}>
            <TrpcProvider>
                <UIProvider>
                    <ProtectedRoutes>
                        <AppProvider />
                    </ProtectedRoutes>
                    <DeferredProductFruitsSetup />
                    <ElementInspector />
                </UIProvider>
            </TrpcProvider>
        </PageController>
    );
}
